import { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { PageNode, Section } from '@/types/sitemap';
import { X, Sparkles, Undo2, Redo2, ChevronRight, FileText, Paperclip, MessageCircle } from 'lucide-react';
import { CommentThread } from './CommentThread';
import { Button } from '@/components/ui/button';
import { RichTextEditor, RichTextEditorHandle } from './RichTextEditor';

interface Props {
  page: PageNode;
  section: Section;
  onUpdateCopy: (pageId: string, sectionId: string, copy: string) => void;
  onGenerateCopy: (pageId: string, sectionId: string) => void;
  onClose: () => void;
  onNavigateToPage?: (pageId: string) => void;
  initialTab?: TabId;
  onTabChange?: (tab: TabId) => void;
}

const MIN_WIDTH = 320;
const MAX_WIDTH_PERCENT = 60;

type TabId = 'editor' | 'files' | 'comments';

export function SectionEditorSidebar({ page, section, onUpdateCopy, onGenerateCopy, onClose, onNavigateToPage, initialTab = 'editor', onTabChange }: Props) {
  const [width, setWidth] = useState(() => Math.round(window.innerWidth * 0.4));
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const editorRef = useRef<RichTextEditorHandle>(null);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = width;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = startX.current - ev.clientX;
      const maxWidth = Math.round(window.innerWidth * (MAX_WIDTH_PERCENT / 100));
      const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth.current + delta));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [width]);

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'editor', label: 'Editor', icon: <FileText size={14} /> },
    { id: 'files', label: 'Files', icon: <Paperclip size={14} /> },
    { id: 'comments', label: 'Comments', icon: <MessageCircle size={14} /> },
  ];

  return (
    <div className="relative bg-card border-l border-border flex flex-col h-full shrink-0" style={{ width }}>
      {/* Resize handle */}
      <div
        onMouseDown={handleResizeStart}
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 hover:bg-primary/20 active:bg-primary/30 transition-colors"
      />

      {/* Header - breadcrumb style */}
      <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0 text-sm">
          <button
            onClick={() => onNavigateToPage?.(page.id)}
            className="text-muted-foreground truncate hover:text-foreground transition-colors cursor-pointer"
          >
            {page.name}
          </button>
          <ChevronRight size={14} className="text-muted-foreground shrink-0" />
          <span className="font-semibold text-foreground truncate">{section.name}</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0"
        >
          <X size={16} />
        </button>
      </div>

      {/* Tabs - pill style matching canvas header */}
      <div className="px-4 py-2 border-b border-border">
        <div className="relative flex items-center bg-muted/50 rounded-lg p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); onTabChange?.(tab.id); }}
              className={`relative z-10 flex items-center gap-1.5 flex-1 justify-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'text-secondary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="section-tab-indicator"
                  className="absolute inset-0 rounded-md bg-secondary"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-1.5">
                {tab.icon}
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'editor' && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Section Copy header with undo/redo */}
          <div className="px-5 pt-4 pb-2 flex items-center justify-between">
            <label className="text-xs font-medium text-foreground">Section Copy</label>
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => editorRef.current?.undo()}
                className="p-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                title="Undo"
              >
                <Undo2 size={14} />
              </button>
              <button
                onClick={() => editorRef.current?.redo()}
                className="p-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                title="Redo"
              >
                <Redo2 size={14} />
              </button>
            </div>
          </div>
          <div className="flex-1 px-5 pb-4 flex flex-col gap-3 overflow-hidden">
            <RichTextEditor
              ref={editorRef}
              content={section.copy}
              onChange={(html) => onUpdateCopy(page.id, section.id, html)}
              placeholder="Enter text or type '/' for commands"
            />
            <Button
              onClick={() => onGenerateCopy(page.id, section.id)}
              className="w-full gap-2 shrink-0 h-11 text-sm font-medium bg-foreground text-background hover:bg-foreground/90"
            >
              <Sparkles size={16} />
              Generate with AI
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'files' && (
        <div className="flex-1 px-5 py-6 flex flex-col items-center justify-center text-center">
          <Paperclip size={32} className="text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">No files yet</p>
          <p className="text-xs text-muted-foreground mb-4">Upload images, PDFs, or other attachments</p>
          <Button variant="outline" size="sm" className="gap-2">
            <Paperclip size={14} />
            Upload File
          </Button>
        </div>
      )}

      {activeTab === 'comments' && (
        <CommentThread key={`section-${page.id}-${section.id}`} threadId={`section-${page.id}-${section.id}`} />
      )}
    </div>
  );
}

